import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { Tooltip } from "react-tooltip";
import { FaEye, FaSpinner } from "react-icons/fa";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";

import styles from "../../SummaryInfoBlock.module.css";
import { formatDate } from "../../../../../HomePage/components/ListBlock/components/ListItem/helpers/formatDate";
import { AutoResizeTextarea } from "../AutoResizeTextarea/AutoResizeTextarea";
import { MarkdownEditor } from "../MarkdownEditor/MarkdownEditor";
import { CustomDatePicker } from "../../../../../HomePage/components/ListBlock/components/Filters/components/CustomDatePicker/CustomDatePicker";
import { IDocDetails } from "../../../../interfaces/IDocDetails";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchDocLink } from "../../services/fetchDocumentLink.service";
import { toast } from "react-toastify";
import { MdGTranslate } from "react-icons/md";
import { TranslateModal } from "../TranslateModal/TranslateModal";
import { fetchDefaultFilterData } from "../../../../../AdminPanelPage/services/defaultFilters.service";
import { BiSave } from "react-icons/bi";
import { AiOutlineEdit } from "react-icons/ai";
import { FiDownload, FiX } from "react-icons/fi";
import { CreateSelectInput } from "./components/CreateSelectInput/CreateSelectInput";

interface IInfoTabProps {
  editableData: any;
  editMode: any;
  setEditableData: (data: IDocDetails) => void;
  setEditMode: (mode: any) => void;
  updatedData: (data: Partial<IDocDetails>) => void;
  onFetchedLink: (link: string) => void;
  handleTranslate: (lang: string) => void;
  isTranslatedFields: boolean;
  loading: boolean;
  onShowOriginal: () => void;
  canUserEdit: boolean;
  discardSignal: number;
}

const InfoTab: React.FC<IInfoTabProps> = ({
  editableData,
  editMode,
  setEditableData,
  setEditMode,
  updatedData,
  onFetchedLink,
  handleTranslate,
  isTranslatedFields,
  loading,
  onShowOriginal,
  canUserEdit,
  discardSignal,
}) => {
  const { t } = useTranslation();
  const originalValuesRef = React.useRef<any>({});
  const previewedLinks = React.useRef<Set<string>>(new Set());
  const [showTranslate, setShowTranslate] = useState(false);
  const darkMode = localStorage.getItem("darkMode") === "true";
  const [currentLang, setCurrentLang] = useState<string | null>(null);
  const [disabledCancelTooltips, setDisabledCancelTooltips] = useState<
    Record<string, boolean>
  >({});

  const { data: defaultFilterData } = useQuery({
    queryKey: ["defaultFilterData"],
    queryFn: () =>
      fetchDefaultFilterData({ regulator: editableData["regulator"] }),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const { mutate: fetchLink } = useMutation({
    mutationFn: async (link: string) => await fetchDocLink(link),
    onError: (error: Error) => {
      console.error("Error fetching document link:", error);
    },
  });

  useEffect(() => {
    if (editableData?.files?.length && previewedLinks.current.size === 0) {
      const firstPreviewFile = editableData.files.find(
        (file: any) => file.preview,
      );
      if (firstPreviewFile?.preview) {
        previewedLinks.current.add(firstPreviewFile.preview);
      }
    }
  }, [editableData.files]);

  const fields = [
    {
      key: "gazette_number",
      label: t("docDetails.officialGazetteNumber"),
      type: "text",
    },
    {
      key: "title",
      label:
        editableData?.code?.includes("gazette") ||
        editableData?.regulator?.includes("Official Gazette")
          ? t("docDetails.title")
          : t("docDetails.caseName"),
      type: "text",
    },
    {
      key: "date",
      label: editableData?.code?.includes("calendar")
        ? t("docDetails.dateOfHearing")
        : t("docDetails.dateOfPublication"),
      type: "date",
    },
    {
      key: "date_issue",
      label: editableData?.code === "pl_sejm"
        ? t("docDetails.latestConsolidatedDate")
        : t("docDetails.dateOfIssue"),
      type: "date",
    },
    {
      key: "signature",
      label: t("docDetails.signature"),
      type: "text",
    },
    {
      key: "city",
      label: t("docDetails.city"),
      type: "text",
    },
    {
      key: "regulator",
      label: t("docDetails.regulator"),
      type: "regulatorInput",
    },
    {
      key: "classes",
      label: t("docDetails.class"),
      type: "classInput",
    },
    {
      key: "keywords",
      label: t("docDetails.keywords"),
      type: "keywordInput",
    },
    {
      key: "tags",
      label: t("newRecordModal.tags"),
      type: "tagInput",
    },
    {
      key: "tax_alert",
      label: t("docDetails.taxAlert"),
      type: "taxAlertInput",
    },
    {
      key: "participants",
      label: t("docDetails.participants"),
      type: "markdown",
    },
    {
      key: "provisions",
      label: t("docDetails.provisions"),
      type: "markdown",
    },
    {
      key: "promulgation",
      label: t("docDetails.promulgation"),
      type: "text",
    },
    {
      key: "status",
      label: t("docDetails.status"),
      type: "text",
    },
    {
      key: "judges",
      label: t("docDetails.judges"),
      type: "judgeInput",
    },
    {
      key: "tech_product",
      label: t("docDetails.techProduct"),
      type: "techProdInput",
    },
    {
      key: "tech_section_vat_act",
      label: t("docDetails.techSectionVATAct"),
      type: "techSecInput",
    },
    {
      key: "subject_matter",
      label: t("docDetails.subjectMatter"),
      type: "text",
    },
    {
      key: "decision",
      label: t("docDetails.decision"),
      type: "markdown",
    },
  ];

  const setUpdatedText = (newValue: any, field: any) => {
    setEditableData({
      ...editableData,
      [field]: newValue || "",
    });
  };

  const setUpdatedDate = (newDate: Date | null, field: string) => {
	setEditableData({
	  ...editableData,
      [field]: newDate ? format(newDate, "yyyy-MM-dd") : "",
    });
  };

  const saveDocInfo = (key: string) => {
    if (!editMode[key]) {
      if (document && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      originalValuesRef.current[key] = editableData[key];
      setEditMode({ ...editMode, [key]: true });

      setDisabledCancelTooltips((prev) => ({ ...prev, [key]: true }));
      setTimeout(() => {
        setDisabledCancelTooltips((prev) => ({ ...prev, [key]: false }));
      }, 300);
    } else {
      if (
        JSON.stringify(editableData[key]) !==
        JSON.stringify(originalValuesRef.current[key])
      ) {
        updatedData({ [key]: editableData[key] || "" });
      }
      setEditMode({ ...editMode, [key]: false });
    }
  };

  const revertDocInfo = (key: string) => {
    if (originalValuesRef.current && key in originalValuesRef.current) {
      setEditableData({
        ...editableData,
        [key]: originalValuesRef.current[key],
      });
    }
    setEditMode({ ...editMode, [key]: false });

    if (document && document.activeElement instanceof HTMLElement) {
      (document.activeElement as HTMLElement).blur();
    }
  };

  useEffect(() => {
    if (!discardSignal) return;
    const keys = Object.keys(editMode).filter(
      (key) => key !== "summary" && key !== "short_summary",
    );
    keys.forEach((key) => {
      if (editMode[key]) revertDocInfo(key);
    });
  }, [discardSignal]);

  const handleFileDownload = (filePath: string) => {
    fetchLink(filePath, {
      onSuccess: (url: string) => {
        const link = document.createElement("a");
        link.href = url;
        link.download = "";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      },
    });
  };

  const handleFilePreview = (filePath: string) => {
    if (previewedLinks.current.has(filePath)) {
      return;
    }
    previewedLinks.current.clear();
    fetchLink(filePath, {
      onSuccess: (url: string) => {
        onFetchedLink(url);
        previewedLinks.current.add(filePath);

        toast.success(t("docDetails.fetchDocLinkToaster"));
      },
    });
  };

  const handleTranslateClick = () => {
    setShowTranslate(true);
  };

  const onTranslate = (selectedLang: string) => {
    if (currentLang === selectedLang) {
      toast.info(t("translateModal.selectedLang"));
    } else {
      setCurrentLang(selectedLang);
      handleTranslate(selectedLang);
    }
  };

  const handleShowOriginal = () => {
    setCurrentLang(null);
    onShowOriginal();
  };

  return (
    <>
      {fields.map(({ key, label, type }) => {
        const value = editableData[key];
        if (value === null) return null;
        if (Object.keys(editableData).length === 0) return null;

        return (
          <div
            key={key}
            className="mb-2 pb-2 border-b-2 border-gray-500 dark:border-gray-300"
          >
            <h4 className="text-black dark:text-white flex items-center justify-between">
              {label}
              <div className="flex items-center gap-2">
                {canUserEdit && key === "decision" && (
                  <>
                    <button
                      className={`tooltip-translate-${key} mr-1 ${isTranslatedFields ? "icon-green" : "icon-gray"}`}
                      onClick={() => handleTranslateClick()}
                    >
                      <MdGTranslate size={16} />
                    </button>
                    <Tooltip
                      anchorSelect={`.tooltip-translate-${key}`}
                      place="top"
                      content={t("translateModal.translate")}
                    />
                  </>
                )}
                {canUserEdit && key in editMode && (
                  <div className="flex items-center">
                    {editMode[key] ? (
                      <>
                        <button
                          onClick={() => revertDocInfo(key)}
                          className="mr-2 text-gray-700 hover:text-gray-500 dark:text-white dark:hover:text-gray-300 icon-gray"
                          data-tooltip-id={`tooltip-cancel-${key}`}
                          data-tooltip-content={
                            disabledCancelTooltips[key]
                              ? ""
                              : t("feedback.cancel")
                          }
                        >
                          <FiX size={16} />
                        </button>
                        <button
                          onClick={() => saveDocInfo(key)}
                          className="mr-2 text-gray-700 hover:text-gray-500 dark:text-white dark:hover:text-gray-300 icon-gray"
                          data-tooltip-id={`tooltip-save-${key}`}
                          data-tooltip-content={t("saveTooltip")}
                        >
                          <BiSave size={16} />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => saveDocInfo(key)}
                        className="mr-2 text-gray-700 hover:text-gray-500 dark:text-white dark:hover:text-gray-300 icon-gray"
                        data-tooltip-id={`tooltip-edit-${key}`}
                        data-tooltip-content={t("editTooltip")}
                      >
                        <AiOutlineEdit size={16} />
                      </button>
                    )}
                  </div>
                )}
                {/* Per-field tooltips using unique ids so they don't get mixed up */}
                <Tooltip id={`tooltip-edit-${key}`} place="top" />
                <Tooltip id={`tooltip-save-${key}`} place="top" />
                <Tooltip id={`tooltip-cancel-${key}`} place="top" />
              </div>
            </h4>
            {editMode[key] ? (
              <>
                {type === "text" && (
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => setUpdatedText(e.target.value, key)}
                    className="text-sm text-secondary-color bg-slate-100 dark:bg-[#2B3544] p-1 rounded w-full"
                  />
                )}
                {type === "textarea" && key !== "provisions" && (
                  <AutoResizeTextarea
                    value={value}
                    onChange={(newVal) => setUpdatedText(newVal, key)}
                  />
                )}
                {type === "markdown" && (
                  <MarkdownEditor
                    data={value ?? ""}
                    onChange={(data: string) => setUpdatedText(data, key)}
                    className="mdx-chat-editor"
                  />
                )}
                {type === "classInput" && (
                  <CreateSelectInput
                    values={typeof value === "string" ? [value] : value}
                    optionsList={defaultFilterData.classes}
                    setValues={(newVal: string[]) =>
                      setUpdatedText(newVal, key)
                    }
                    placeholderKey={t("filtersSection.searchAndSelectClass")}
                  />
                )}
                {type === "tagInput" && (
                  <CreateSelectInput
                    values={value}
                    optionsList={defaultFilterData.keywords}
                    setValues={(newVal: string[]) =>
                      setUpdatedText(newVal, key)
                    }
                    placeholderKey={t("filtersSection.searchAndSelectTags")}
                  />
                )}
                {type === "keywordInput" && (
                  <CreateSelectInput
                    values={value}
                    optionsList={defaultFilterData.keywords}
                    setValues={(newVal: string[]) =>
                      setUpdatedText(newVal, key)
                    }
                    placeholderKey={t("filtersSection.searchAndSelectKeywords")}
                  />
                )}
                {type === "techProdInput" && (
                  <CreateSelectInput
                    values={value}
                    optionsList={defaultFilterData.tech_product}
                    setValues={(newVal: string[]) =>
                      setUpdatedText(newVal, key)
                    }
                    placeholderKey={t(
                      "filtersSection.searchAndSelectTechProduct",
                    )}
                  />
                )}
                {type === "techSecInput" && (
                  <CreateSelectInput
                    values={value}
                    optionsList={defaultFilterData.tech_section_vat_act}
                    setValues={(newVal: string[]) =>
                      setUpdatedText(newVal, key)
                    }
                    placeholderKey={t(
                      "filtersSection.searchAndSelectTechSection",
                    )}
                  />
                )}
                {type === "taxAlertInput" && (
                  <CreateSelectInput
                    values={value}
                    optionsList={defaultFilterData.tax_alert}
                    setValues={(newVal: string[]) =>
                      setUpdatedText(newVal, key)
                    }
                    placeholderKey={t("filtersSection.searchAndSelectTaxAlert")}
                  />
                )}
				{type === "date" && (
				  <CustomDatePicker
				    startDate={value ? new Date(value) : null}
					isRange={false}
					onChange={(date: Date) => setUpdatedDate(date, key)}
					withPortal={false}
				  />
				)}
              </>
            ) : (
              <>
                {key === "decision" && loading ? (
                  <div className="text-center p-4">
                    <FaSpinner
                      className="animate-spin inline-block mr-2"
                      size={24}
                      color={darkMode ? "#FFFFFF" : "#000000"}
                    />
                  </div>
                ) : key === "provisions" ? (
                  <Markdown
                    rehypePlugins={[rehypeRaw]}
                    remarkPlugins={[remarkGfm, remarkBreaks]}
                    className={
                      styles.markdownBlock +
                      " text-secondary-color pl-0.5 text-sm"
                    }
                  >
                    {value ? value?.replace(/^---/, "") : ""}
                  </Markdown>
                ) : type === "text" || type === "textarea" ? (
                  <p className="text-sm text-secondary-color">{value}</p>
                ) : type === "date" ? (
                  <p className="text-sm text-secondary-color">
                    {formatDate(value)}
                  </p>
                ) : key === "keywords" ? (
                  <p className="text-sm text-secondary-color">
                    {value?.join(", ")}
                  </p>
                ) : key === "tags" ? (
                  <p className="text-sm text-secondary-color">
                    {value?.join(", ")}
                  </p>
                ) : key === "judges" ? (
                  <p className="text-sm text-secondary-color">
                    {value?.join(", ")}
                  </p>
                ) : key === "classes" ? (
                  <p className="text-sm text-secondary-color">
                    {value?.join(", ")}
                  </p>
                ) : key === "tax_alert" ? (
                  <p className="text-sm text-secondary-color">
                    {value?.join(", ")}
                  </p>
                ) : key === "tech_section_vat_act" ? (
                  <p className="text-sm text-secondary-color">
                    {value?.join(", ")}
                  </p>
                ) : key === "tech_product" ? (
                  <p className="text-sm text-secondary-color">
                    {value?.join(", ")}
                  </p>
                ) : key === "regulator" ? (
                  <p className="text-sm text-secondary-color">{value}</p>
                ) : (
                  <Markdown
                    rehypePlugins={[rehypeRaw]}
                    remarkPlugins={[remarkGfm, remarkBreaks]}
                    className={
                      styles.markdownBlock + " text-secondary-color text-sm"
                    }
                  >
                    {typeof value === "string" ? value : ""}
                  </Markdown>
                )}
              </>
            )}
          </div>
        );
      })}
      {editableData.code === "lotus_notes" &&
        Array.isArray(editableData.files) && (
          <div className="mb-2 pb-2 border-b-2 border-gray-500 dark:border-gray-300">
            <h4 className="title-base-color">
              {t("docDetails.lotusNotesLink")}
            </h4>
            {editableData.files.length === 0 ? (
              <p className="text-sm text-secondary-color">{t("noData")}</p>
            ) : (
              <ul className="list-disc list-inside">
                {editableData.files.map((file: any, index: number) => {
                  const displayName = file.original.replace(
                    /^lotus_notes\/Rep_[^/]+\/?/,
                    "",
                  );
                  const isActivePreview = previewedLinks.current.has(
                    file.preview,
                  );

                  return (
                    <li
                      key={index}
                      className="list-none flex justify-between items-center text-sm text-secondary-color whitespace-nowrap overflow-hidden text-ellipsis mb-1"
                    >
                      <span className="cursor-pointer overflow-hidden text-ellipsis">
                        {displayName}
                      </span>

                      <span className="flex gap-2 ml-2">
                        {file.preview && (
                          <button
                            onClick={() => {
                              if (file.preview) handleFilePreview(file.preview);
                            }}
                            className="text-gray-600 hover:text-del-green"
                          >
                            <FaEye
                              className={`${isActivePreview ? "text-del-green" : ""} w-4 h-4`}
                            />
                          </button>
                        )}
                        <button
                          onClick={() => handleFileDownload(file.original)}
                          className="text-gray-600 hover:text-del-green mr-2"
                        >
                          <FiDownload className="w-4 h-4" />
                        </button>
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

      {typeof editableData.link === "string" &&
        editableData.link.trim() !== "" &&
        editableData.link !== "null" && (
          <div className="mb-1 pb-2">
            <h4 className="title-base-color">{t("docDetails.link")}</h4>
            <a
              href={editableData.link}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-sm text-secondary-color whitespace-nowrap overflow-hidden text-ellipsis tooltip-text hover:underline"
            >
              {editableData.link}
            </a>
            <Tooltip
              anchorSelect={`.tooltip-text`}
              place="top-end"
              className="text-xs font-light"
              opacity={0.5}
              content={editableData.link}
            />
          </div>
        )}
      <TranslateModal
        isOpen={showTranslate}
        onClose={() => setShowTranslate(false)}
        onShowOriginal={handleShowOriginal}
        onSelectTranslatedLang={onTranslate}
      />
    </>
  );
};

export { InfoTab };
